(function () {
  function initNpdp(root) {
    if (root.dataset.npdpInit === '1') return;
    root.dataset.npdpInit = '1';

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ---------- per-character title cascade ----------
       Runs BEFORE the reveal logic on purpose. The reveal adds .is-in to the title,
       and `.npdp__title.is-in .npdp__ch` is what animates the characters. If the
       characters were created *after* .is-in was already present they would be
       inserted already matching their end state, and the browser would paint them
       finished instead of transitioning — the cascade would silently disappear. */
    var title = root.querySelector('[data-npdp-cascade]');
    if (title && !reduce) {
      var words = (title.textContent || '').trim().split(/\s+/);
      var i = 0;
      title.textContent = '';
      words.forEach(function (word, w) {
        var wrap = document.createElement('span');
        wrap.className = 'npdp__word';
        word.split('').forEach(function (ch) {
          var s = document.createElement('span');
          s.className = 'npdp__ch';
          s.style.setProperty('--ci', i++);
          s.textContent = ch;
          wrap.appendChild(s);
        });
        title.appendChild(wrap);
        if (w < words.length - 1) {
          var sp = document.createElement('span');
          sp.className = 'npdp__ch';
          sp.style.setProperty('--ci', i++);
          sp.innerHTML = '&nbsp;';
          title.appendChild(sp);
        }
      });
    }

    /* ---------- scroll reveals ----------
       Anything already on screen at load is revealed immediately rather than being
       handed to the IntersectionObserver. The observer's first callback is async, so
       above-the-fold content (breadcrumbs, brand, title) used to sit at opacity 0 for
       a beat after the page had visibly painted. Content in the initial viewport must
       be readable at a glance; only genuinely below-the-fold blocks should wait.

       The two nested rAFs give the freshly-created title characters one frame to have
       their initial styles computed, so adding .is-in transitions them instead of
       snapping. Two frames is ~32ms — imperceptible, and far shorter than the observer
       round-trip it replaces. */
    var revealables = Array.prototype.slice.call(root.querySelectorAll('.npdp-reveal'));
    if (reduce || !('IntersectionObserver' in window)) {
      revealables.forEach(function (el) { el.classList.add('is-in'); });
    } else {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var onscreen = [];
      var offscreen = [];
      revealables.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < vh && r.bottom > 0) onscreen.push(el); else offscreen.push(el);
      });

      /* threshold 0 + a positive bottom margin: start the reveal just before a block
         scrolls into view. npdp.css's old `-10%` bottom margin held the trigger back
         until the block was already well inside the viewport, which is what made the
         USP rows feel like they were lagging behind the scroll. A 0.05 threshold also
         delayed tall blocks (the panel) far more than short ones. */
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px 10% 0px', threshold: 0 });
      offscreen.forEach(function (el) { io.observe(el); });

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          onscreen.forEach(function (el) { el.classList.add('is-in'); });
        });
      });
    }

    /* ---------- gallery ---------- */
    var track = root.querySelector('[data-npdp-track]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-npdp-slide]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-npdp-thumb]'));
    var prev = root.querySelector('[data-npdp-prev]');
    var next = root.querySelector('[data-npdp-next]');
    var index = 0;
    var progress = root.querySelector('[data-npdp-progress]');

    function paint(n) {
      index = Math.max(0, Math.min(n, slides.length - 1));
      if (progress) progress.style.setProperty('--i', String(index));
      slides.forEach(function (s, k) { s.dataset.active = k === index ? 'true' : 'false'; });
      thumbs.forEach(function (t, k) { t.setAttribute('aria-current', k === index ? 'true' : 'false'); });
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
      var active = thumbs[index];
      if (active && active.parentNode) {
        var p = active.parentNode;
        var left = active.offsetLeft - (p.clientWidth - active.clientWidth) / 2;
        p.scrollTo({ left: left, behavior: reduce ? 'auto' : 'smooth' });
      }
    }

    function goTo(n) {
      if (!track || !slides.length) return;
      var target = slides[Math.max(0, Math.min(n, slides.length - 1))];
      if (!target) return;
      if (target.offsetParent === null || track.scrollWidth <= track.clientWidth + 1) { paint(n); return; }
      track.scrollTo({ left: target.offsetLeft - track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
      paint(n);
    }

    if (track) {
      var raf = null;
      track.addEventListener('scroll', function () {
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = null;
          var mid = track.scrollLeft + track.clientWidth / 2;
          var best = 0, bestD = Infinity;
          slides.forEach(function (s, k) {
            if (s.offsetParent === null) return;
            var c = s.offsetLeft - track.offsetLeft + s.clientWidth / 2;
            var d = Math.abs(c - mid);
            if (d < bestD) { bestD = d; best = k; }
          });
          if (best !== index) paint(best);
        });
      }, { passive: true });

      track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
      });
    }

    if (prev) prev.addEventListener('click', function () { goTo(index - 1); });
    if (next) next.addEventListener('click', function () { goTo(index + 1); });
    thumbs.forEach(function (t, k) { t.addEventListener('click', function () { goTo(k); }); });
    slides.forEach(function (s, k) {
      s.addEventListener('click', function () { openLightbox(k); });
    });
    paint(0);

    /* ---------- lightbox: big image + thumbnail rail ---------- */
    var lb = root.querySelector('[data-npdp-layer="lightbox"]');
    var lbItems = Array.prototype.slice.call(root.querySelectorAll('[data-npdp-lb-item]'));
    var lbThumbs = Array.prototype.slice.call(root.querySelectorAll('[data-npdp-lb-thumb]'));
    var lbRail = root.querySelector('[data-npdp-lb-rail]');
    var lbCount = root.querySelector('[data-npdp-lb-count]');
    var lbPrev = root.querySelector('[data-npdp-lb-prev]');
    var lbNext = root.querySelector('[data-npdp-lb-next]');
    var lbIndex = 0;

    function lbPaint(n) {
      if (!lbItems.length) return;
      lbIndex = (n + lbItems.length) % lbItems.length;
      lbItems.forEach(function (it, k) {
        var on = k === lbIndex;
        it.dataset.active = on ? 'true' : 'false';
        var vid = it.querySelector('video');
        if (vid && !on && !vid.paused) vid.pause();
      });
      lbThumbs.forEach(function (t, k) {
        t.setAttribute('aria-current', k === lbIndex ? 'true' : 'false');
      });
      if (lbCount) lbCount.textContent = (lbIndex + 1) + ' / ' + lbItems.length;
      if (lbPrev) lbPrev.disabled = lbItems.length < 2;
      if (lbNext) lbNext.disabled = lbItems.length < 2;
      var at = lbThumbs[lbIndex];
      if (at && lbRail) {
        var left = at.offsetLeft - (lbRail.clientWidth - at.clientWidth) / 2;
        lbRail.scrollTo({ left: left, behavior: reduce ? 'auto' : 'smooth' });
      }
      // load the neighbours lazily so paging feels instant
      [lbIndex - 1, lbIndex, lbIndex + 1].forEach(function (k) {
        var it = lbItems[(k + lbItems.length) % lbItems.length];
        var img = it && it.querySelector('img[data-src]');
        if (img) { img.src = img.getAttribute('data-src'); img.removeAttribute('data-src'); }
      });
    }

    function openLightbox(n) {
      lbPaint(n || 0);
      openLayer('lightbox');
      // keep the inline gallery in sync when the lightbox closes
      goTo(lbIndex);
    }

    root.querySelectorAll('[data-npdp-lb-open]').forEach(function (b) {
      b.addEventListener('click', function () { openLightbox(parseInt(b.getAttribute('data-npdp-lb-open'), 10) || 0); });
    });

    if (lbPrev) lbPrev.addEventListener('click', function () { lbPaint(lbIndex - 1); });
    if (lbNext) lbNext.addEventListener('click', function () { lbPaint(lbIndex + 1); });
    lbThumbs.forEach(function (t, k) { t.addEventListener('click', function () { lbPaint(k); }); });

    if (lb) {
      lb.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); lbPaint(lbIndex - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); lbPaint(lbIndex + 1); }
      });
      // swipe on touch
      var sx = null, sy = null;
      lb.addEventListener('touchstart', function (e) {
        sx = e.touches[0].clientX; sy = e.touches[0].clientY;
      }, { passive: true });
      lb.addEventListener('touchend', function (e) {
        if (sx === null) return;
        var dx = e.changedTouches[0].clientX - sx;
        var dy = e.changedTouches[0].clientY - sy;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) lbPaint(lbIndex + (dx < 0 ? 1 : -1));
        sx = sy = null;
      }, { passive: true });
    }

    /* ---------- accordions (single-open) ---------- */
    var heads = Array.prototype.slice.call(root.querySelectorAll('[data-npdp-acc-head]'));
    function setAcc(head, open) {
      var body = head.parentNode.querySelector('[data-npdp-acc-body]');
      if (!body) return;
      head.setAttribute('aria-expanded', open ? 'true' : 'false');
      var inner = body.firstElementChild;
      body.style.height = open ? (inner ? inner.offsetHeight + 'px' : 'auto') : '0px';
    }
    heads.forEach(function (head) {
      head.addEventListener('click', function () {
        var isOpen = head.getAttribute('aria-expanded') === 'true';
        heads.forEach(function (h) { setAcc(h, false); });
        if (!isOpen) setAcc(head, true);
      });
    });
    // initial + keep the open one correctly sized on resize / font load
    function syncAcc() {
      heads.forEach(function (h) { setAcc(h, h.getAttribute('aria-expanded') === 'true'); });
    }
    syncAcc();
    window.addEventListener('resize', syncAcc);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(syncAcc);

    /* ---------- overlays ---------- */
    var lastFocus = null;
    /* Anchored popover (condition info icon): opens right under the hearts row and follows it
       on scroll; flips above when there is no room below. The original placed it at fixed
       viewport corners (top:300px / bottom:40px), i.e. nowhere near the icon and on phones
       behind the sticky buy bar. Coordinates are taken relative to the layer's own box, so
       this also holds if an ancestor's transform turns position:fixed into a local frame. */
    var anchorBtn = null, anchoredLayer = null;
    function placeAnchored(layer) {
      var pop = layer.querySelector('.npdp__popover');
      if (!pop || !anchorBtn) return;
      anchoredLayer = layer;
      layer.classList.add('is-anchored');
      var row = anchorBtn.closest('.npdp__condition') || anchorBtn;
      var m = 16, gap = 10;
      var vw = document.documentElement.clientWidth, vh = window.innerHeight;
      var w = Math.min(root.classList.contains('npdp--v3') ? 540 : 360, vw - 2 * m);
      pop.style.width = w + 'px';
      pop.style.right = 'auto';
      pop.style.bottom = 'auto';
      pop.style.maxHeight = 'none';
      var r = row.getBoundingClientRect(), base = layer.getBoundingClientRect();
      var h = pop.offsetHeight;
      var left = Math.max(m, Math.min(r.left, vw - w - m));
      var below = vh - r.bottom - gap - m, above = r.top - gap - m;
      var flip = h > below && above > below;
      var top = flip ? Math.max(m, r.top - gap - h) : r.bottom + gap;
      var room = flip ? above : below;
      if (h > room) pop.style.maxHeight = Math.max(160, room) + 'px';
      layer.classList.toggle('is-flipped', flip);
      pop.style.left = (left - base.left) + 'px';
      pop.style.top = (top - base.top) + 'px';
    }
    function reanchor() { if (anchoredLayer && !anchoredLayer.hidden) placeAnchored(anchoredLayer); }
    function onOutside(e) {
      if (!anchoredLayer) return;
      var pop = anchoredLayer.querySelector('.npdp__popover');
      if (pop.contains(e.target) || (anchorBtn && anchorBtn.contains(e.target))) return;
      closeLayer(anchoredLayer);
    }
    function unanchor() {
      anchoredLayer = null;
      window.removeEventListener('scroll', reanchor);
      window.removeEventListener('resize', reanchor);
      document.removeEventListener('click', onOutside, true);
    }

    function openLayer(name) {
      var layer = root.querySelector('[data-npdp-layer="' + name + '"]');
      if (!layer) return;
      lastFocus = document.activeElement;
      layer.hidden = false;
      if (anchorBtn) {
        placeAnchored(layer);
        window.addEventListener('scroll', reanchor, { passive: true });
        window.addEventListener('resize', reanchor);
        document.addEventListener('click', onOutside, true);
      }
      requestAnimationFrame(function () { layer.classList.add('is-open'); });
      var close = layer.querySelector('[data-npdp-close]');
      if (close) close.focus({ preventScroll: true });
      document.addEventListener('keydown', onEsc);
    }
    function closeLayer(layer) {
      if (layer === anchoredLayer) unanchor();
      layer.classList.remove('is-open');
      var done = function () { layer.hidden = true; };
      if (reduce) done(); else setTimeout(done, 325);
      document.removeEventListener('keydown', onEsc);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    function onEsc(e) {
      if (e.key !== 'Escape') return;
      root.querySelectorAll('[data-npdp-layer]:not([hidden])').forEach(closeLayer);
    }
    root.querySelectorAll('[data-npdp-open]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = root.querySelector('[data-npdp-layer="' + btn.getAttribute('data-npdp-open') + '"]:not([hidden])');
        if (open && btn.hasAttribute('data-npdp-anchor')) { closeLayer(open); return; }
        anchorBtn = btn.hasAttribute('data-npdp-anchor') ? btn : null;
        openLayer(btn.getAttribute('data-npdp-open'));
      });
    });
    root.querySelectorAll('[data-npdp-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { closeLayer(btn.closest('[data-npdp-layer]')); });
    });
    root.querySelectorAll('[data-npdp-layer]').forEach(function (layer) {
      layer.addEventListener('click', function (e) { if (e.target === layer) closeLayer(layer); });
    });

    /* ---------- sticky bar ---------- */
    var sticky = root.querySelector('[data-npdp-sticky]');
    if (sticky) {
      /* Show the bar once the real buy button has scrolled out of view, rather than
         at a fixed scroll offset -- a hardcoded threshold fires at the wrong moment
         on short pages and on mobile, where the buy block sits much further down. */
      var buyBlock = root.querySelector('.npdp__buy') || root.querySelector('.npdp__info');
      if (buyBlock && 'IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            var scrolledPast = !e.isIntersecting && e.boundingClientRect.top < 0;
            sticky.classList.toggle('is-in', scrolledPast);
          });
        }, { threshold: 0 }).observe(buyBlock);
      } else {
        var tick = null;
        var onScroll = function () {
          if (tick) return;
          tick = requestAnimationFrame(function () {
            tick = null;
            sticky.classList.toggle('is-in', window.scrollY > 750);
          });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
    }

    /* ---------- v3: close-up strip (desktop) ----------
       Progress segment = visible share of the strip, moved by the scroll position; arrows page by
       the strip's width and hide at either end. */
    var dstrip = root.querySelector('[data-npdp-dstrip-track]');
    if (dstrip) {
      var dbar = root.querySelector('[data-npdp-dstrip-progress]');
      var dprev = root.querySelector('[data-npdp-dstrip-prev]');
      var dnext = root.querySelector('[data-npdp-dstrip-next]');
      var paintStrip = function () {
        var max = dstrip.scrollWidth - dstrip.clientWidth;
        var share = dstrip.scrollWidth ? dstrip.clientWidth / dstrip.scrollWidth : 1;
        if (dbar) {
          dbar.style.setProperty('--w', (share * 100) + '%');
          dbar.style.setProperty('--x', (max > 0 ? (dstrip.scrollLeft / max) * ((1 - share) / share) * 100 : 0) + '%');
        }
        if (dprev) dprev.hidden = dstrip.scrollLeft <= 2;
        if (dnext) dnext.hidden = dstrip.scrollLeft >= max - 2;
      };
      dstrip.addEventListener('scroll', function () { requestAnimationFrame(paintStrip); }, { passive: true });
      window.addEventListener('resize', paintStrip);
      if (dprev) dprev.addEventListener('click', function () { dstrip.scrollBy({ left: -dstrip.clientWidth * 0.86, behavior: reduce ? 'auto' : 'smooth' }); });
      if (dnext) dnext.addEventListener('click', function () { dstrip.scrollBy({ left: dstrip.clientWidth * 0.86, behavior: reduce ? 'auto' : 'smooth' }); });
      paintStrip();
    }

    /* ---------- v3: sticky info column ----------
       top = header + 24px while the column fits the screen. A taller column gets a negative
       top, so it scrolls with the page and then sticks with its bottom edge 24px above the
       viewport's: nothing is ever cut off. Re-measured when an accordion opens. */
    var infoCol = root.classList.contains('npdp--v3') ? root.querySelector('.npdp__info') : null;
    if (infoCol) {
      var placeInfo = function () {
        var cs = getComputedStyle(document.documentElement);
        var head = parseFloat(cs.getPropertyValue('--sticky-area-height')) || 0;
        var vh = window.innerHeight;
        var top = Math.min(head + 24, vh - infoCol.offsetHeight - 24);
        infoCol.style.setProperty('--npdp-stick', Math.round(top) + 'px');
      };
      placeInfo();
      window.addEventListener('resize', placeInfo);
      if ('ResizeObserver' in window) new ResizeObserver(placeInfo).observe(infoCol);
    }

    /* ---------- v3: delivery date ----------
       Today + data-days working days (Mon–Fri), written in the page language. Conservative on purpose:
       the upper end of the delivery window, no same-day assumption. */
    root.querySelectorAll('[data-npdp-deliv]').forEach(function (el) {
      var days = parseInt(el.getAttribute('data-days'), 10) || 3;
      var d = new Date();
      var n = 0;
      while (n < days) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) n++;
      }
      var slot = el.querySelector('[data-npdp-deliv-date]');
      try {
        /* "Di. 3. Nov." / "Tue 3 Nov" / "mar 3 nov": weekday, day, month in that order, no commas */
        var lang = document.documentElement.lang || 'de';
        var part = function (o) { return new Intl.DateTimeFormat(lang, o).format(d).replace(/,/g, ''); };
        slot.textContent = part({ weekday: 'short' }) + ' ' + d.getDate() + (/^de/.test(lang) ? '.' : '') + ' ' + part({ month: 'short' });
        el.hidden = false;
        var fb = el.parentNode && el.parentNode.querySelector('[data-npdp-deliv-fallback]');
        if (fb) fb.hidden = true;
      } catch (e) { /* no Intl: the plain shipping text stays */ }
    });

    /* Sold out: the Wunschliste snippet (wunsch-notify) puts its own "Benachrichtige mich" into the panel
       and hides the disabled cart button there. v3.23 removed the old takeover here: it un-disabled that
       button, which defeated the snippet's hide rule — two notify buttons on phones (30.10.2026). */

    /* ---------- v3: phone buy panel ----------
       Below 992px the sticky bar is a permanent bottom panel (COS). Its height goes to
       --npdp-panel-h on <html> so the page can pad its end and the chat bubble can sit above it. */
    if (sticky && root.classList.contains('npdp--v3')) {
      var mqPanel = window.matchMedia('(max-width: 991px)');
      var setPanel = function () {
        var h = mqPanel.matches ? sticky.offsetHeight : 0;
        document.documentElement.style.setProperty('--npdp-panel-h', h + 'px');
      };
      setPanel();
      if ('ResizeObserver' in window) new ResizeObserver(setPanel).observe(sticky);
      if (mqPanel.addEventListener) mqPanel.addEventListener('change', setPanel);
      window.addEventListener('resize', setPanel);
    }

    /* ---------- add-to-cart is handled by Instant's runtime ----------
       All three ATC controls (main, sticky, mobile quick-add) are anchors
       carrying data-instant-action-type="add-to-instant-cart", so instant-core.js
       binds them directly and opens the Instant cart drawer. Do NOT proxy clicks
       between them here — that would add the item twice. */

    /* ---------- variant picker ---------- */
    root.querySelectorAll('[data-npdp-option-value]').forEach(function (input) {
      input.addEventListener('change', function () {
        var url = input.getAttribute('data-variant-url');
        if (url) window.location.href = url;
      });
    });
    }

  function boot() {
    document.querySelectorAll('[data-npdp]').forEach(initNpdp);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // re-init after Shopify section re-render in the theme editor
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-npdp]').forEach(initNpdp);
  });
})();
